import React, { useState, useEffect } from "react";

import { connect } from "react-redux";
import history from "../../history";

const Shifts = ({ appData: { candidateId } }) => {
  const [iframeUrl, setIframeUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(600); // Default height
  const [currentWeekEnding, setCurrentWeekEnding] = useState(null);
  const [currentWeekStarting, setCurrentWeekStarting] = useState(null);
  const [mobileSelectedDate, setMobileSelectedDate] = useState(null);

  // Helper function to get nearest Saturday (week ending)
  const getNearestSaturday = (date = new Date()) => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const daysUntilSaturday = (6 - dayOfWeek) % 7;
    const saturday = new Date(date);
    saturday.setDate(date.getDate() + daysUntilSaturday);
    return saturday;
  };

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  // Helper function to add days to a date
  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  // Initialize week dates
  useEffect(() => {
    const weekEnding = getNearestSaturday();
    const weekStarting = addDays(weekEnding, -6);
    setCurrentWeekEnding(weekEnding);
    setCurrentWeekStarting(weekStarting);
    setMobileSelectedDate(weekStarting);
  }, []);

  // Update iframe URL when week changes
  useEffect(() => {
    if (!currentWeekEnding || !currentWeekStarting) return;

    // Get the base URL dynamically from the current window location
    const getBaseUrl = () => {
      const { protocol, hostname, port } = window.location;
      const portString = port ? `:${port}` : "";
      return `${protocol}//${hostname}${portString}`;
    };

    const baseUrl = getBaseUrl();
    const weekEndingStr = formatDate(currentWeekEnding);
    const weekStartingStr = formatDate(currentWeekStarting);
    const mobileSelectedDateStr = formatDate(mobileSelectedDate);

    const shiftPlannerUrl = `${baseUrl}/my-account/shifts?candidate_id=${candidateId}&week_ending=${weekEndingStr}&week_starting=${weekStartingStr}&mobile_selected_date=${mobileSelectedDateStr}`;

    setIframeUrl(shiftPlannerUrl);
    setLoading(false);
  }, [candidateId, currentWeekEnding, currentWeekStarting, mobileSelectedDate]);

  useEffect(() => {
    const handleMessage = (event) => {
      const allowedOrigins = [
        "https://font.localhost:4001",
        "https://font.staging.hrgolabs.dev",
        "https://font.demo.hrgolabs.dev",
        "https://font.hrgolabs.dev",
        "https://staging.hrgolabs.dev",
        "https://www.hrgo.co.uk",
      ];

      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data.type === "iframe-height") {
        const minHeight = 400;
        const newHeight = Math.max(event.data.height, minHeight);
        setIframeHeight(newHeight);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleIframeLoad = () => {
    setLoading(false);
    setError(null);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(
      "Failed to load shifts. Please check your authentication or try refreshing the page."
    );
  };

  // Navigation functions
  const goToPrevWeek = () => {
    if (!currentWeekEnding) return;
    const newWeekEnding = addDays(currentWeekEnding, -7);
    const newWeekStarting = addDays(newWeekEnding, -6);
    setCurrentWeekEnding(newWeekEnding);
    setCurrentWeekStarting(newWeekStarting);
    setMobileSelectedDate(newWeekStarting);
  };

  const goToNextWeek = () => {
    if (!currentWeekEnding) return;
    const newWeekEnding = addDays(currentWeekEnding, 7);
    const newWeekStarting = addDays(newWeekEnding, -6);
    setCurrentWeekEnding(newWeekEnding);
    setCurrentWeekStarting(newWeekStarting);
    setMobileSelectedDate(newWeekStarting);
  };

  // Mobile day navigation functions
  const goToPrevDay = () => {
    if (!mobileSelectedDate || !currentWeekStarting || !currentWeekEnding)
      return;

    const prevDay = addDays(mobileSelectedDate, -1);

    // If going before the current week, move to previous week
    if (prevDay < currentWeekStarting) {
      const newWeekEnding = addDays(currentWeekEnding, -7);
      const newWeekStarting = addDays(newWeekEnding, -6);
      setCurrentWeekEnding(newWeekEnding);
      setCurrentWeekStarting(newWeekStarting);
      setMobileSelectedDate(prevDay);
    } else {
      setMobileSelectedDate(prevDay);
    }
  };

  const goToNextDay = () => {
    if (!mobileSelectedDate || !currentWeekStarting || !currentWeekEnding)
      return;

    const nextDay = addDays(mobileSelectedDate, 1);

    // If going after the current week, move to next week
    if (nextDay > currentWeekEnding) {
      const newWeekEnding = addDays(currentWeekEnding, 7);
      const newWeekStarting = addDays(newWeekEnding, -6);
      setCurrentWeekEnding(newWeekEnding);
      setCurrentWeekStarting(newWeekStarting);
      setMobileSelectedDate(nextDay);
    } else {
      setMobileSelectedDate(nextDay);
    }
  };

  // Helper function to get month name
  const getMonthName = (date) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[date.getMonth()];
  };

  // Helper function to get day name
  const getDayName = (date) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[date.getDay()];
  };

  // Helper function to format date for display
  const formatDateForDisplay = (date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="container">
      <header
        className="row mb-1"
        style={{
          color: "#495057",
          letterSpacing: "0.6px",
          lineHeight: "19px",
        }}
      >
        <h2 className="mb-2 mt-2 col-md-7 col-sm-10">Shifts</h2>
      </header>
      {/* Desktop Navigation Header */}
      {currentWeekEnding && (
        <div className="mb-3 d-none d-md-block">
          <div className="d-flex align-items-center justify-content-between rounded">
            <button
              onClick={goToPrevWeek}
              className="btn btn-outline-info"
              disabled={loading}
              style={{ color: "#212121" }}
            >
              ← Previous Week
            </button>

            <button
              onClick={goToNextWeek}
              className="btn btn-outline-info"
              disabled={loading}
              style={{ color: "#212121" }}
            >
              Next Week →
            </button>
          </div>
        </div>
      )}

      {/* Mobile Navigation Header */}
      {mobileSelectedDate && (
        <div className="mb-3 d-md-none">
          <div className="d-flex align-items-center justify-content-between bg-light p-3 rounded">
            <button
              onClick={goToPrevDay}
              className="btn btn-outline-info"
              disabled={loading}
              style={{ color: "#212121" }}
            >
              ← Previous Day
            </button>

            <button
              onClick={goToNextDay}
              className="btn btn-outline-info"
              style={{ color: "#212121" }}
              disabled={loading}
            >
              Next Day →
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading shifts...</span>
          </div>
          <p className="mt-2">Loading your shifts...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Unable to load shifts</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            <a
              href={iframeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Open Shifts in New Tab
            </a>
          </p>
        </div>
      )}

      {iframeUrl && (
        <iframe
          src={iframeUrl}
          title="Shifts Scheduler"
          className="w-100"
          allow="same-origin"
          style={{
            marginTop: "28px",
            height: `${iframeHeight}px`,
            border: "none",
            borderRadius: "0.375rem",
            display: loading || error ? "none" : "block",
            transition: "height 0.3s ease", // Smooth height transitions
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      )}

      <div
        className="container"
        style={{
          display: "flex",
          padding: "30px 0",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          onClick={() => history.push("/profile")}
          className="btn"
          style={{ backgroundColor: "lightgray" }}
        >
          Back to My Account
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const withRedux = connect(mapStateToProps, null);

export default withRedux(Shifts);
